import { Eye, EyeOff } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/atoms/button";
import { Input } from "@/components/ui/atoms/input";

type ClientSecretInputProps = React.ComponentProps<"input"> & {
  /** "create" requires a value; "rotate" treats empty as "keep existing" */
  mode: "create" | "rotate";
  /** When mode="rotate", show the amber "A secret is set" pill */
  secretIsSet?: boolean;
};

export type { ClientSecretInputProps };

export const ClientSecretInput = ({
  mode,
  secretIsSet,
  disabled,
  ...inputProps
}: ClientSecretInputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  let placeholder =
    "Leave empty to keep current, or enter new secret to replace";
  if (mode === "create") {
    placeholder = "Enter client secret";
  } else if (disabled) {
    placeholder = "Client secret is configured and cannot be modified";
  }

  return (
    <div className="space-y-2">
      {!!(mode === "rotate" && (secretIsSet ?? false)) && (
        <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="font-medium text-xs">A secret is set</span>
          </div>
        </div>
      )}
      <div className="relative">
        <Input
          autoComplete="client-secret"
          disabled={disabled}
          placeholder={placeholder}
          type={showPassword ? "text" : "password"}
          {...inputProps}
        />
        <Button
          aria-label={showPassword ? "Hide secret" : "Show secret"}
          className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1"
          disabled={disabled}
          onClick={() => setShowPassword((s) => !s)}
          size="sm"
          type="button"
          variant="ghost"
        >
          {showPassword ? (
            <EyeOff className="size-4" />
          ) : (
            <Eye className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
};
